"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFetchData } from "../../../hooks/useFetch";
import { useViewportMaxHeight } from "../../../hooks/useViewportMaxHeight";
import { isSuccess } from "../../../models/response/base-response";
import { ApiConfig, API_DRIVER_CASH_LEDGER_ROUTER } from "../../../services/api-config";
import BranchService, { Branch } from "../../branch/services/branch-service";
import DateInputCustom from "../../../components/custom/date-input/date-input-custom";
import EmptyState from "../../../components/custom/empty-state";
import { showToast } from "../../../components/custom/custom-toast";
import { formatCurrency, removeVietnameseFromString } from "../../../utils/helpers";
import { downloadOrderServiceExcel } from "../../order-list/utils/download-excel";
import { TABLE_CLASS, TD, TD_NUM, TF, TH, THEAD_CLASS } from "../../sales-output-report/components/service-table";
import CashLedgerTransactionsModal from "../components/cash-ledger-transactions-modal";

/**
 * Sổ quỹ theo chi nhánh / nhân viên — tồn đầu kỳ · thu · nộp · tồn cuối kỳ của sổ quỹ tiền mặt nhân viên.
 * BE: accounting `CashLedgerReportService`. Chi nhánh = chi nhánh CHỦ ĐƠN của khoản tiền (đơn của chi nhánh nào thì
 * tiền tính cho chi nhánh đó); nhân viên thu ở nhiều chi nhánh được tách mỗi chi nhánh một dòng.
 */

interface Amounts {
  opening: number;
  collected: number;
  deposited: number;
  other: number;
  closing: number;
  transaction_count: number;
}

interface BranchRow extends Amounts {
  branch_id: number | null;
  branch_name: string;
  employee_count: number;
}

interface EmployeeRow extends Amounts {
  driver_id: number;
  driver_name: string;
  branch_id: number | null;
  branch_name: string;
}

interface Report {
  summary: Amounts;
  by_branch: BranchRow[];
  by_employee: EmployeeRow[];
}

type Tab = "branch" | "employee";

const EMPTY: Amounts = { opening: 0, collected: 0, deposited: 0, other: 0, closing: 0, transaction_count: 0 };

function extractList(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw?.list && Array.isArray(raw.list)) return raw.list;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  return [];
}

// DateInputCustom dùng dd/MM/yyyy; BE nhận YYYY-MM-DD → convert ở ranh giới.
const toDMY = (ymd: string) => {
  const [y, m, d] = (ymd || "").split("-");
  return d && m && y ? `${d}/${m}/${y}` : "";
};
const toYMD = (dmy: string) => {
  const [d, m, y] = (dmy || "").split("/");
  return d && m && y ? `${y}-${m}-${d}` : "";
};

const getDefaultDates = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
  return { from: fmt(firstDay), to: fmt(now) };
};

const normalize = (v: string) => removeVietnameseFromString(v || "").toLowerCase();
const money = (v: number) => (Number(v) ? formatCurrency(v) : "-");

const VIEW_BUTTON =
  "whitespace-nowrap rounded-lg border border-emerald-200 px-2.5 py-1 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-50";

/** 4 cột tiền + số GD, dùng chung cho 2 bảng. */
const AmountCells: React.FC<{ r: Amounts }> = ({ r }) => (
  <>
    <td className={TD_NUM}>{money(r.opening)}</td>
    <td className={`${TD_NUM} text-emerald-600`}>{money(r.collected)}</td>
    <td className={`${TD_NUM} text-red-500`}>{money(r.deposited)}</td>
    <td className={`${TD_NUM} font-semibold ${r.closing > 0 ? "text-blue-700" : "text-gray-500"}`}>{formatCurrency(r.closing)}</td>
    <td className={`${TD} text-center`}>{r.transaction_count}</td>
  </>
);

const AmountHeaders = () => (
  <>
    <th className={TH}>Tồn đầu kỳ (đ)</th>
    <th className={TH}>Thu (đ)</th>
    <th className={TH}>Nộp (đ)</th>
    <th className={TH}>Tồn cuối kỳ (đ)</th>
    <th className={TH}>Số GD</th>
  </>
);

const AmountTotals: React.FC<{ s: Amounts }> = ({ s }) => (
  <>
    <td className={`${TF} border-t-2 border-t-blue-200`}>{formatCurrency(s.opening)}</td>
    <td className={`${TF} border-t-2 border-t-blue-200 text-emerald-700`}>{formatCurrency(s.collected)}</td>
    <td className={`${TF} border-t-2 border-t-blue-200 text-red-600`}>{formatCurrency(s.deposited)}</td>
    <td className={`${TF} border-t-2 border-t-blue-200 text-blue-900`}>{formatCurrency(s.closing)}</td>
    <td className={`${TF} border-t-2 border-t-blue-200 text-center`}>{s.transaction_count}</td>
  </>
);

const CashLedgerReportPage: React.FC = () => {
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.from);
  const [endDate, setEndDate] = useState(defaults.to);
  const [selectedBranchId, setSelectedBranchId] = useState<number | "">("");
  const [branches, setBranches] = useState<Branch[]>([]);
  const [report, setReport] = useState<Report | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tab, setTab] = useState<Tab>("branch");
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState<EmployeeRow | null>(null);

  const { ref: frameRef, maxHeight } = useViewportMaxHeight<HTMLDivElement>(40);

  const { request: fetchReport } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.ACCOUNTING_SERVICE });
  // Chỉ chi nhánh user được phụ trách (BE cũng giới hạn lại theo token).
  const { fetch: listBranches } = BranchService.myBranches();
  const fetchRef = useRef(fetchReport);
  const branchRef = useRef(listBranches);
  useEffect(() => { fetchRef.current = fetchReport; }, [fetchReport]);
  useEffect(() => { branchRef.current = listBranches; }, [listBranches]);

  useEffect(() => {
    (async () => {
      const res = await branchRef.current();
      if (res && isSuccess(res)) setBranches(extractList(res.data));
    })();
  }, []);

  const loadReport = useCallback(async () => {
    if (!startDate || !endDate) return;
    setIsLoading(true);
    try {
      const params: Record<string, unknown> = { from_date: startDate, to_date: endDate };
      if (selectedBranchId) params.branch_id = selectedBranchId;
      const res = await fetchRef.current({ endPoint: API_DRIVER_CASH_LEDGER_ROUTER.API_REPORT, data: params });
      if (res && isSuccess(res)) setReport(res.data ?? null);
      else showToast({ message: (res as any)?.message || "Không tải được sổ quỹ", type: "error" });
    } catch (e) {
      console.error("Failed to load cash ledger report:", e);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, selectedBranchId]);

  useEffect(() => { void loadReport(); }, [loadReport]);

  const summary = report?.summary ?? EMPTY;

  const branchRows = useMemo(() => {
    const list = report?.by_branch ?? [];
    const q = normalize(search.trim());
    return q ? list.filter((r) => normalize(r.branch_name).includes(q)) : list;
  }, [report, search]);

  const employeeRows = useMemo(() => {
    const list = report?.by_employee ?? [];
    const q = normalize(search.trim());
    return q ? list.filter((r) => [r.driver_name, r.branch_name].some((f) => normalize(f).includes(q))) : list;
  }, [report, search]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const params: Record<string, unknown> = { from_date: startDate, to_date: endDate };
      if (selectedBranchId) params.branch_id = selectedBranchId;
      await downloadOrderServiceExcel(
        API_DRIVER_CASH_LEDGER_ROUTER.API_REPORT_EXPORT,
        params,
        `so-quy_${startDate}_${endDate}.xlsx`,
        ApiConfig.PROJECT_ID.ACCOUNTING_SERVICE,
      );
    } catch (e: any) {
      showToast({ message: e?.message || "Xuất Excel thất bại", type: "error" });
    } finally {
      setExporting(false);
    }
  };

  const cards = [
    { label: "Tồn đầu kỳ", value: summary.opening, tone: "text-gray-900", sign: "" },
    { label: "Tổng thu", value: summary.collected, tone: "text-emerald-600", sign: "+" },
    { label: "Tổng nộp", value: summary.deposited, tone: "text-red-500", sign: "-" },
    { label: "Tồn cuối kỳ", value: summary.closing, tone: "text-blue-700", sign: "" },
  ];

  const tabButton = (key: Tab, label: string, count: number) => (
    <button
      key={key}
      type="button"
      onClick={() => setTab(key)}
      className={`border-b-2 pb-1 text-sm font-semibold transition-colors ${
        tab === key ? "border-emerald-600 text-emerald-700" : "border-transparent text-gray-500 hover:text-gray-700"
      }`}
    >
      {label} <span className="ml-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-700">{count}</span>
    </button>
  );

  const renderBranchTable = () => (
    <table className={TABLE_CLASS}>
      <thead className={THEAD_CLASS}>
        <tr>
          <th className={`${TH} w-14`}>STT</th>
          <th className={`${TH} min-w-[220px]`}>Chi nhánh</th>
          <th className={TH}>Số NV</th>
          <AmountHeaders />
          <th className={TH}></th>
        </tr>
      </thead>
      <tbody>
        {branchRows.map((b, i) => (
          <tr key={b.branch_id ?? "unknown"} className="hover:bg-gray-50">
            <td className={`${TD} text-center text-gray-400`}>{i + 1}</td>
            <td className={`${TD} font-medium ${b.branch_id ? "text-gray-900" : "italic text-amber-700"}`}>{b.branch_name}</td>
            <td className={`${TD} text-center`}>{b.employee_count}</td>
            <AmountCells r={b} />
            <td className={`${TD} text-center`}>
              <button
                type="button"
                onClick={() => {
                  // Chi nhánh thật → lọc cả trang về chi nhánh đó; nhóm chưa xác định → chỉ tìm trong tab nhân viên.
                  if (b.branch_id) setSelectedBranchId(b.branch_id);
                  setSearch(b.branch_id ? "" : b.branch_name);
                  setTab("employee");
                }}
                className={VIEW_BUTTON}
              >
                Xem nhân viên
              </button>
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={3} className={`${TF} border-t-2 border-t-blue-200`}>Tổng cộng ({report?.by_branch.length ?? 0} chi nhánh)</td>
          <AmountTotals s={summary} />
          <td className={`${TF} border-t-2 border-t-blue-200 border-r-0`}></td>
        </tr>
      </tfoot>
    </table>
  );

  const renderEmployeeTable = () => (
    <table className={TABLE_CLASS}>
      <thead className={THEAD_CLASS}>
        <tr>
          <th className={`${TH} w-14`}>STT</th>
          <th className={`${TH} min-w-[180px]`}>Nhân viên</th>
          <th className={`${TH} min-w-[180px]`}>Chi nhánh</th>
          <AmountHeaders />
          <th className={TH}></th>
        </tr>
      </thead>
      <tbody>
        {employeeRows.map((e, i) => (
          <tr key={`${e.driver_id}-${e.branch_id ?? "unknown"}`} className="hover:bg-gray-50">
            <td className={`${TD} text-center text-gray-400`}>{i + 1}</td>
            <td className={`${TD} font-medium text-gray-900`}>{e.driver_name}</td>
            <td className={`${TD} ${e.branch_id ? "text-gray-600" : "italic text-amber-700"}`}>{e.branch_name}</td>
            <AmountCells r={e} />
            <td className={`${TD} text-center`}>
              <button type="button" onClick={() => setDetail(e)} className={VIEW_BUTTON}>
                Xem giao dịch
              </button>
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={3} className={`${TF} border-t-2 border-t-blue-200`}>Tổng cộng ({report?.by_employee.length ?? 0} dòng)</td>
          <AmountTotals s={summary} />
          <td className={`${TF} border-t-2 border-t-blue-200 border-r-0`}></td>
        </tr>
      </tfoot>
    </table>
  );

  const rowsOfTab = tab === "branch" ? branchRows.length : employeeRows.length;

  return (
    <div className="space-y-6">
      {/* Header + bộ lọc */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Sổ quỹ tiền mặt</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Tồn đầu kỳ · thu · nộp · tồn cuối kỳ theo chi nhánh chủ đơn và theo nhân viên
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportExcel}
              disabled={exporting || !startDate || !endDate}
              className="flex items-center gap-2 h-10 px-4 bg-green-600 text-white text-sm font-medium rounded-xl hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {exporting ? "Đang xuất..." : "Xuất Excel"}
            </button>
            <button
              onClick={loadReport}
              disabled={isLoading}
              aria-label="Tải lại"
              className="flex items-center justify-center w-10 h-10 bg-white border border-gray-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-colors"
            >
              <svg className={`w-5 h-5 text-gray-600 ${isLoading ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Khoảng ngày</label>
            <div className="flex items-center gap-2">
              <DateInputCustom
                type="date"
                value={toDMY(startDate)}
                maxDate={toDMY(endDate) || undefined}
                onChange={(d) => setStartDate(toYMD(d ?? ""))}
                className="w-[150px]"
                placeholder="Từ ngày"
              />
              <span className="text-slate-400">→</span>
              <DateInputCustom
                type="date"
                value={toDMY(endDate)}
                minDate={toDMY(startDate) || undefined}
                onChange={(d) => setEndDate(toYMD(d ?? ""))}
                className="w-[150px]"
                placeholder="Đến ngày"
              />
            </div>
          </div>
          <div className="w-full sm:w-[280px]">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Chi nhánh</label>
            <select
              value={selectedBranchId}
              onChange={(e) => setSelectedBranchId(e.target.value ? Number(e.target.value) : "")}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white transition-colors"
            >
              <option value="">Tất cả chi nhánh phụ trách</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Thẻ tổng */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-sm font-medium text-gray-500">{c.label}</p>
            <p className={`text-2xl font-bold mt-1 ${c.tone}`}>
              {c.value ? c.sign : ""}{formatCurrency(c.value)} <span className="text-sm font-normal text-gray-400">đ</span>
            </p>
          </div>
        ))}
      </div>

      {/* Bảng */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-lg bg-slate-50 px-6 py-3">
          <div className="flex flex-wrap gap-6">
            {tabButton("branch", "Theo chi nhánh", branchRows.length)}
            {tabButton("employee", "Theo nhân viên", employeeRows.length)}
          </div>
          <div className="relative w-full sm:w-[300px]">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
              </svg>
            </div>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tab === "branch" ? "Tìm chi nhánh..." : "Tìm nhân viên, chi nhánh..."}
              className="w-full rounded-xl border border-gray-200 bg-white pl-9 pr-4 py-2.5 text-sm shadow-sm placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all"
            />
          </div>
        </div>

        <div ref={frameRef} style={{ maxHeight }} className="mt-4 overflow-auto rounded-lg border border-gray-200 bg-white">
          {isLoading && !report ? (
            <div className="py-16 text-center text-sm text-gray-400">Đang tải...</div>
          ) : rowsOfTab === 0 ? (
            <EmptyState
              message={search.trim() ? "Không tìm thấy kết quả" : "Chưa có phát sinh sổ quỹ"}
              description={
                search.trim()
                  ? "Không có dòng nào khớp từ khoá tìm kiếm."
                  : "Không có tồn quỹ hay giao dịch nào trong khoảng thời gian và chi nhánh đã chọn."
              }
            />
          ) : (
            <div className={isLoading ? "opacity-60 transition-opacity" : "transition-opacity"}>
              {tab === "branch" ? renderBranchTable() : renderEmployeeTable()}
            </div>
          )}
        </div>
        <p className="mt-2 text-xs text-gray-400">
          Chi nhánh = chi nhánh chủ đơn của khoản tiền (khoản nộp theo khoản thu được nộp). "Chưa xác định chi nhánh":
          khoản nộp kiểu cũ không gắn khoản thu, hoặc dữ liệu cũ chưa được gán chi nhánh.
        </p>
      </div>

      {detail && (
        <CashLedgerTransactionsModal
          driverId={detail.driver_id}
          driverName={detail.driver_name}
          branchId={detail.branch_id}
          branchName={detail.branch_name}
          startDate={startDate}
          endDate={endDate}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
};

export default CashLedgerReportPage;
